import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TestQuestion, TestQuestionsResponse } from '../../services/api/test';

// Mock test data
const mockTestData: TestQuestionsResponse = {
  questions: [
    {
      questionId: 'q1',
      questionType: 'pinyin-recognition',
      questionContent: 'Choose the correct pinyin: 人',
      options: ['rén', 'rěn', 'rèn', 'rēn'],
      correctAnswer: 'rén',
      explanation: 'The pinyin of "人" is "rén", meaning human.',
    },
    {
      questionId: 'q2',
      questionType: 'character-recognition',
      questionContent: 'Choose the correct character: shān',
      options: ['山', '出', '上', '三'],
      correctAnswer: '山',
      explanation: 'The character "山" is the pinyin of "shān", meaning mountain.',
    },
    {
      questionId: 'q3',
      questionType: 'pinyin-recognition',
      questionContent: 'Choose the correct pinyin: 水',
      options: ['suǐ', 'shuǐ', 'suí', 'shuí'],
      correctAnswer: 'shuǐ',
      explanation: 'The pinyin of "水" is "shuǐ", meaning liquid.',
    },
    {
      questionId: 'q4',
      questionType: 'character-recognition',
      questionContent: 'Choose the correct character: tiān',
      options: ['田', '天', '夫', '大'],
      correctAnswer: '天',
      explanation: 'The character "天" is the pinyin of "tiān", meaning sky.',
    },
  ],
  totalTime: 300,
  totalNum: 4,
};

const TestPage: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(300);

  useEffect(() => {
    // 加载测试题目
    const loadQuestions = async () => {
      try {
        setIsLoading(true);
        // 使用mock数据替代API调用
        setQuestions(mockTestData.questions);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load questions');
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestions();
  }, []);

  // 倒计时定时器
  useEffect(() => {
    if (timeRemaining > 0 && questions.length > 0) {
      const timer = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0) {
      // 时间到，自动提交
      handleSubmit();
    }
  }, [timeRemaining, questions.length]);

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const answerList = Object.entries(answers).map(([questionId, selectedAnswer]) => ({
        questionId,
        selectedAnswer,
      }));

      // 模拟提交测试结果
      console.log('提交测试结果:', { answerList, usedTime: 300 - timeRemaining });
      
      // 这里可以添加提交到服务器的逻辑
      // await testApi.submitTest({ userId: 'user1', answerList, usedTime: 300 - timeRemaining });

      // 跳转到结果页面
      navigate('/test/result', {
        state: {
          answers,
          questions,
          usedTime: 300 - timeRemaining,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen">Loading test questions...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center min-h-screen text-red-600">{error}</div>;
  }

  if (questions.length === 0) {
    return <div className="flex justify-center items-center min-h-screen">No test questions available</div>;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion.questionId];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">HSK Level Test</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm font-medium text-gray-700">
                Time remaining: {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-500">
                Question {currentQuestionIndex + 1} / {questions.length}
              </span>
              <span className="text-sm font-medium text-indigo-600">
                {currentQuestion.questionType === 'pinyin-recognition' ? 'Pinyin Recognition' : 'Character Recognition'}
              </span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">{currentQuestion.questionContent}</h2>
          </div>

          <div className="space-y-4 mb-6">
            {currentQuestion.options.map((option, index) => (
              <div key={index}>
                <input
                  type="radio"
                  id={`option-${index}`}
                  name="answer"
                  value={option}
                  checked={currentAnswer === option}
                  onChange={() => handleAnswerChange(currentQuestion.questionId, option)}
                  className="mr-2"
                />
                <label htmlFor={`option-${index}`} className="text-gray-700">
                  {option}
                </label>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center">
            <button
              onClick={handlePrev}
              disabled={currentQuestionIndex === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              Previous
            </button>

            {currentQuestionIndex === questions.length - 1 ? (
              <button
                onClick={handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                Submit
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TestPage;
